import { metalCommitmentLocationQueryAtom } from "@/admin/metal/commitments/commitmentsQuery.ts";
import { type AdminMetalMachineItemLoc, metalMachineLocationQueryAtom } from "@/admin/metal/machine/machinesQuery.ts";
import { getCurrentCommitment, type MetalCommitmentWithEndsAt } from "@/modules/metal/commitment/commitment.ts";
import { metalOsQueryAtom } from "@/modules/metal/metalOsQuery.ts";
import { mergeLoadedQueries, type QMergedResult } from "@/utils/query/mergeQueries.ts";
import type { AdminMetalMachineItem, MetalOs, MetalOsType } from "@warrenio/api-spec/spec.oats.gen";
import { mapOnValues } from "@warrenio/utils/collections/maps";
import { atomFamily } from "jotai-family";
import { atom } from "jotai/vanilla";

export interface ItemOsInfo {
    os_type: MetalOsType | undefined;
    os: MetalOs | undefined;
}

export interface AdminMachineWithOs extends AdminMetalMachineItemLoc, ItemOsInfo {
    period_hours?: MetalCommitmentWithEndsAt["period_hours"];
    ends_at?: MetalCommitmentWithEndsAt["ends_at"];
}

export type AdminMachineWithOsResponse = Map<AdminMetalMachineItem["uuid"], AdminMachineWithOs>;

export const machineWithOsAtom = atomFamily((location: string) => {
    const machinesQuery = metalMachineLocationQueryAtom(location);
    const commitmentsQuery = metalCommitmentLocationQueryAtom(location);

    return atom((get): QMergedResult<AdminMachineWithOsResponse | undefined> => {
        const results = {
            machines: get(machinesQuery),
            commitments: get(commitmentsQuery),
            osData: get(metalOsQueryAtom),
        };

        return mergeLoadedQueries(results, ({ machines, commitments, osData }) => {
            return mapOnValues(machines, (item): AdminMachineWithOs => {
                const leaseUuid = item.lease_uuid ?? item.lease?.uuid;
                const commitment = leaseUuid ? getCurrentCommitment(commitments, leaseUuid) : null;
                const os = osData.os.find((o) => o.os_id === item.os_id);
                const os_type = osData.os_types.find((o) => o.os_type_id === os?.os_type_id);
                return {
                    ...item,
                    period_hours: commitment?.period_hours,
                    ends_at: commitment?.ends_at,
                    os_type,
                    os,
                };
            });
        });
    });
});
